import React, { useCallback, useEffect } from 'react';
import * as RadixPopover from '@radix-ui/react-popover';
import { HOT_KEY, useHotKey, useHotKeyIsolate } from '../HotKeys';
import { StyledContent } from './Popover.view';

/**
 * Компонент Popover — обёртка над Radix UI Popover, предоставляющая декларативный API
 * для управления состоянием открытия/закрытия, а также стилизованное содержимое.
 *
 * Использование:
 *
 * <Popover
 *   isOpen={isPopoverOpen}
 *   onOpen={() => console.log('Открыт')}
 *   onClose={() => console.log('Закрыт')}
 *   onOpenChange={(open) => setIsPopoverOpen(open)}
 * >
 *   <Popover.Trigger>
 *     <button>Открыть поповер</button>
 *   </Popover.Trigger>
 *   <Popover.Content side="top" align="center">
 *     <div>Контент поповера</div>
 *   </Popover.Content>
 * </Popover>
 *
 * Основные особенности:
 * - Управляемый режим: через проп `isOpen` можно контролировать открытие извне.
 * - Колбэки `onOpen`, `onClose`, `onOpenChange` позволяют отслеживать изменения состояния.
 * - `Popover.Trigger` — обёртка над Radix Trigger с `asChild`, позволяющая использовать произвольный элемент.
 * - `Popover.Content` — стилизованное содержимое, с параметрами позиционирования (`side`, `align`, `sideOffset`, `alignOffset`).
 * - Используется `RadixPopover.Portal` для рендеринга контента вне DOM-иерархии родителя.
 *
 * Важно:
 * - `StyledContent` должен быть реализован с учётом Radix Popover positioning props.
 * - `modal={true}` означает, что поповер блокирует взаимодействие с остальной страницей (ARIA modal behavior).
 */

export interface PopoverProps {
    children: React.ReactNode;
    isOpen?: boolean;

    onClose?: () => void;

    onOpen?: () => void;
    modal?: boolean;

    onOpenChange?(isOpen: boolean): void;
}

export const Popover = ({
    children,
    onClose,
    isOpen: extIsOpen = false,
    onOpen,
    onOpenChange,
    modal = true,
}: PopoverProps) => {
    const [isOpen, setIsOpen] = React.useState(extIsOpen);
    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            onOpenChange?.(open);
            if (open) {
                onOpen?.();
            } else {
                onClose?.();
            }
        },
        [onClose, onOpen, onOpenChange],
    );

    useEffect(() => {
        setIsOpen(extIsOpen);
    }, [extIsOpen]);

    useHotKeyIsolate('popover', {
        enabled: isOpen,
    });

    useHotKey(
        HOT_KEY.ESC,
        () => {
            handleOpenChange(false);
        },
        {
            stopPropagation: true,
            enabled: isOpen,
            handlerName: 'close-popover',
        },
        [isOpen, handleOpenChange],
    );

    return (
        <RadixPopover.Root
            modal={modal}
            open={isOpen}
            onOpenChange={handleOpenChange}
        >
            {children}
        </RadixPopover.Root>
    );
};

type PopoverTriggerProps = {
    children: React.ReactNode;
};

Popover.Trigger = ({ children }: PopoverTriggerProps) => (
    <RadixPopover.Trigger asChild>{children}</RadixPopover.Trigger>
);

type PopoverContentProps = {
    children: React.ReactNode;
    side?: 'top' | 'right' | 'bottom' | 'left';
    align?: 'start' | 'center' | 'end';
    sideOffset?: number;
    alignOffset?: number;
    withPortal?: boolean;
    onFocusOutside?: (event: Event) => void;
    onCloseAutoFocus?: (event: Event) => void;
    container?: HTMLElement;
};
Popover.Content = ({
    children,
    side = 'bottom',
    align = 'center',
    sideOffset = 4,
    alignOffset = 0,
    withPortal = true,
    onFocusOutside,
    onCloseAutoFocus,
    container,
}: PopoverContentProps) => {
    if (withPortal) {
        return (
            <RadixPopover.Portal container={container}>
                <StyledContent
                    side={side}
                    align={align}
                    sideOffset={sideOffset}
                    alignOffset={alignOffset}
                    onFocusOutside={onFocusOutside}
                    onCloseAutoFocus={onCloseAutoFocus}
                >
                    {children}
                </StyledContent>
            </RadixPopover.Portal>
        );
    } else {
        return (
            <StyledContent
                side={side}
                align={align}
                sideOffset={sideOffset}
                alignOffset={alignOffset}
                onFocusOutside={onFocusOutside}
                onCloseAutoFocus={onCloseAutoFocus}
            >
                {children}
            </StyledContent>
        );
    }
};
